(function () {
  'use strict';

  var registry = window.TodayMerchPreviewRenderers = window.TodayMerchPreviewRenderers || {};
  if (registry.print) return;

  // Visual family only. Availability and artwork qualification belong to the
  // selected product/variant/zone, never to this renderer.
  var TECHNIQUE_IDS = Object.freeze(['pad_print', 'pad-print', 'pad_print_drinkware', 'digital_print_2']);
  var MAX_PIXELS = 4194304;
  var MAX_SIDE = 4096;
  var CHUNK_PIXELS = 65536;
  var metadata = new WeakMap();
  var TEXTILE_PROCESSES = Object.freeze(['dtg', 'screen-print', 'sublimation']);
  var MATERIALS = Object.freeze({
    ceramic: { grain: 0.0015, reflection: 0.8 },
    metal: { grain: 0.003, reflection: 0.9 },
    plastic: { grain: 0.0025, reflection: 0.6 },
    glass: { grain: 0.001, reflection: 0.85 },
    paper: { grain: 0.006, reflection: 0.1 },
    wood: { grain: 0.005, reflection: 0.15 },
    textile: { grain: 0.009, reflection: 0.05 },
    cotton: { grain: 0.011, reflection: 0.04 },
    polyester: { grain: 0.007, reflection: 0.08 },
    unknown: { grain: 0.001, reflection: 0 }
  });

  function number(value, fallback, min, max) {
    return typeof value === 'number' && Number.isFinite(value) ? Math.max(min, Math.min(max, value)) : fallback;
  }

  function own(object, key) {
    return typeof key === 'string' && Object.prototype.hasOwnProperty.call(object, key);
  }

  function isCanvas(value) {
    var view = value && value.ownerDocument && value.ownerDocument.defaultView;
    return Boolean(view && value instanceof view.HTMLCanvasElement);
  }

  function supportsTechnique(id) {
    return typeof id === 'string' && TECHNIQUE_IDS.indexOf(id) !== -1;
  }

  function visualProfile(options) {
    var profile = options.visualProfile;
    if (!profile || profile.verified !== true || TEXTILE_PROCESSES.indexOf(profile.process) === -1
      || typeof options.techniqueId !== 'string' || !options.techniqueId
      || profile.techniqueId !== options.techniqueId || typeof profile.source !== 'string' || !profile.source.trim()
      || options.qualification?.status !== 'allowed' || options.qualification?.compatible !== true
      || ['textile', 'cotton', 'polyester'].indexOf(options.material) === -1) return null;
    // A caller-supplied, evidence-bound visual profile is not a catalogue alias
    // or proof that this technique can manufacture the selected design.
    return { process: profile.process, source: profile.source };
  }

  function checkAbort(signal) {
    if (signal && signal.aborted) throw new DOMException('The print preview was cancelled.', 'AbortError');
  }

  function pause() {
    // Timers also run in hidden tabs. A frame callback could stall cancellation
    // indefinitely when the product gallery is no longer visible.
    return new Promise(function (resolve) { window.setTimeout(resolve, 0); });
  }

  function colorLightness(value) {
    if (typeof value !== 'string' || !/^#[\da-f]{3}(?:[\da-f]{3})?$/i.test(value)) return 0.5;
    var hex = value.slice(1);
    if (hex.length === 3) hex = hex.split('').map(function (part) { return part + part; }).join('');
    return (parseInt(hex.slice(0, 2), 16) * 0.2126 + parseInt(hex.slice(2, 4), 16) * 0.7152 + parseInt(hex.slice(4, 6), 16) * 0.0722) / 255;
  }

  function settings(options) {
    var material = own(MATERIALS, options.material) ? options.material : 'unknown';
    var lighting = options.lighting && typeof options.lighting === 'object' ? options.lighting : {};
    var direction = Array.isArray(lighting.direction) ? lighting.direction : [-0.6, -0.8];
    var x = number(direction[0], -0.6, -1, 1);
    var y = number(direction[1], -0.8, -1, 1);
    var length = Math.sqrt(x * x + y * y);
    if (length > 1) { x /= length; y /= length; }
    var finish = ['matte', 'satin', 'gloss'].indexOf(options.finish) !== -1 ? options.finish : 'matte';
    var quality = ['draft', 'standard', 'high'].indexOf(options.quality) !== -1 ? options.quality : 'standard';
    return {
      material: MATERIALS[material],
      strength: number(options.strength, 0.65, 0, 1),
      lighting: number(lighting.strength, 0.5, 0, 1),
      x: x, y: y,
      // A finish describes a known surface. It does not add a coating or ink
      // capability. Unknown material has no invented specular reflection.
      sheen: (finish === 'gloss' ? 1 : finish === 'satin' ? 0.4 : 0) * MATERIALS[material].reflection,
      darkness: 1 - colorLightness(options.supportColor),
      quality: quality
    };
  }

  function surfaceField(source, document, width, height, quality) {
    // A same-size caller-supplied crop can carry actual product lighting. Never
    // fetch an image or infer a surface colour from the artwork itself.
    if (!isCanvas(source) || source.width !== width || source.height !== height || !width || !height) return null;
    var sample = document.createElement('canvas');
    var limit = quality === 'high' ? 128 : 64;
    var scale = Math.min(1, limit / Math.max(width, height));
    sample.width = Math.max(1, Math.round(width * scale));
    sample.height = Math.max(1, Math.round(height * scale));
    var context = sample.getContext('2d', { willReadFrequently: true });
    if (!context) return null;
    var pixels;
    try {
      context.drawImage(source, 0, 0, sample.width, sample.height);
      pixels = context.getImageData(0, 0, sample.width, sample.height).data;
    } catch (_) {
      // An unreadable optional crop never invalidates a readable logo.
      return null;
    }
    var luminance = new Float32Array(sample.width * sample.height);
    var sum = 0;
    var weight = 0;
    for (var i = 0; i < luminance.length; i++) {
      var offset = i * 4;
      var alpha = pixels[offset + 3] / 255;
      var value = (pixels[offset] * 0.2126 + pixels[offset + 1] * 0.7152 + pixels[offset + 2] * 0.0722) / 255;
      luminance[i] = alpha ? value : -1;
      sum += value * alpha;
      weight += alpha;
    }
    if (!weight) return null;
    return { values: luminance, width: sample.width, height: sample.height, mean: sum / weight };
  }

  function fieldAt(field, x, y, width, height) {
    if (!field) return 0;
    var sx = (x + 0.5) / width * field.width - 0.5;
    var sy = (y + 0.5) / height * field.height - 0.5;
    var x0 = Math.max(0, Math.min(field.width - 1, Math.floor(sx)));
    var y0 = Math.max(0, Math.min(field.height - 1, Math.floor(sy)));
    var x1 = Math.min(field.width - 1, x0 + 1);
    var y1 = Math.min(field.height - 1, y0 + 1);
    var fx = Math.max(0, Math.min(1, sx - x0));
    var fy = Math.max(0, Math.min(1, sy - y0));
    function value(px, py) {
      var sample = field.values[py * field.width + px];
      return sample < 0 ? field.mean : sample;
    }
    var top = value(x0, y0) * (1 - fx) + value(x1, y0) * fx;
    var bottom = value(x0, y1) * (1 - fx) + value(x1, y1) * fx;
    return Math.max(-1, Math.min(1, ((top * (1 - fy) + bottom * fy) - field.mean) * 2));
  }

  function grainAt(x, y) {
    // Stable, bounded microtexture; no noise canvas, edge growth or blur.
    var hash = Math.imul(x + 1, 374761393) ^ Math.imul(y + 1, 668265263);
    hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
    return ((hash ^ (hash >>> 16)) >>> 0) / 4294967295 * 2 - 1;
  }

  async function render(sourceCanvas, options) {
    options = options && typeof options === 'object' ? options : {};
    var signal = options.signal;
    checkAbort(signal);
    if (!isCanvas(sourceCanvas)) throw new TypeError('A source HTMLCanvasElement is required.');
    var width = sourceCanvas.width;
    var height = sourceCanvas.height;
    if (width > MAX_SIDE || height > MAX_SIDE || width * height > MAX_PIXELS) {
      throw new RangeError('The print preview is limited to 4096 pixels per side and 4194304 pixels.');
    }
    var document = sourceCanvas.ownerDocument;
    var config = settings(options);
    var knownTechnique = supportsTechnique(options.techniqueId);
    var profile = visualProfile(options);
    var incompatible = options.qualification && (options.qualification.compatible === false
      || (options.qualification.status != null && options.qualification.status !== 'allowed'));
    var shouldRender = config.strength !== 0 && options.qualified !== false
      && !incompatible
      && (options.visualProfile ? Boolean(profile) : (options.techniqueId == null || supportsTechnique(options.techniqueId)));
    var output = document.createElement('canvas');
    output.width = width;
    output.height = height;
    var context = output.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('A 2D canvas context is required.');
    function finish(applied, reason) {
      metadata.set(output, Object.freeze({ previewOnly: true, applied: applied, reason: reason,
        process: profile?.process || (knownTechnique ? 'print' : null),
        geometryPreserved: true, effectiveArtworkUnchanged: true,
        limitations: Object.freeze(profile?.process === 'sublimation'
          ? ['simulation_only', 'screen_colors_not_proof', 'substrate_and_white_areas_unverified', 'no_fabrication_approval']
          : ['simulation_only', 'screen_colors_not_proof', 'no_underbase_prediction', 'no_fabrication_approval']) }));
      return output;
    }
    var method = profile?.process || null;
    // Snapshot once before yielding. The source and its production metadata are
    // never mutated, even if another preview starts while this one is running.
    if (width && height) context.drawImage(sourceCanvas, 0, 0);
    // Freeze the lighting field as well as primitive settings before yielding:
    // callers may reuse a canvas/options object for the next selection.
    var field = shouldRender ? surfaceField(options.surfaceCanvas, document, width, height, config.quality) : null;
    await pause();
    checkAbort(signal);
    if (!width || !height || !shouldRender) return finish(false, !width || !height ? 'empty_source' : 'visual_profile_unqualified');

    var image;
    try { image = context.getImageData(0, 0, width, height); }
    catch (error) { if (error.name === 'SecurityError') return finish(false, 'pixels_unreadable'); throw error; }
    var data = image.data;
    var rows = Math.max(1, Math.floor(CHUNK_PIXELS / width));
    for (var start = 0; start < height; start += rows) {
      checkAbort(signal);
      var end = Math.min(height, start + rows);
      for (var y = start; y < end; y++) {
        var ny = (y + 0.5) / height - 0.5;
        for (var x = 0; x < width; x++) {
          var index = (y * width + x) * 4;
          if (!data[index + 3]) continue;
          var nx = (x + 0.5) / width - 0.5;
          // Achromatic illumination keeps the artwork's colour relationships.
          // Its alpha, silhouette, antialiasing and geometry remain untouched.
          var light = Math.max(-1, Math.min(1, (nx * config.x + ny * config.y) * 1.4));
          var texture = config.quality === 'draft' ? 0 : grainAt(x, y) * config.material.grain;
          if (method && config.quality !== 'draft') {
            // Display texture in normalized artwork space; never a physical
            // weave/stitch size inferred from the photo or a supplier threshold.
            var weave = Math.sin(x * 1.6) * Math.cos(y * 1.6);
            texture += weave * (method === 'dtg' ? 0.012 : method === 'sublimation' ? 0.009 : 0.003);
            if (method === 'screen-print') texture *= 0.45;
          }
          var absorption = 0.012 + config.darkness * 0.004;
          var shade = absorption + (1 - light) * 0.013 * config.lighting
            - fieldAt(field, x, y, width, height) * 0.022 * config.lighting - texture;
          // Never force an opaque white rectangle, underbase, metallic tint,
          // embossed border or new ink colour onto the effective artwork.
          var gain = 1 - Math.max(0, Math.min(0.075, shade * config.strength));
          var sheen = Math.max(0, light) * config.sheen * config.darkness * config.lighting * config.strength * 1.5;
          for (var channel = 0; channel < 3; channel++) data[index + channel] = Math.round(data[index + channel] * gain + sheen);
        }
      }
      if (end < height) { await pause(); checkAbort(signal); }
    }
    checkAbort(signal);
    context.putImageData(image, 0, 0);
    return finish(true, 'simulated');
  }

  registry.print = Object.freeze({
    version: '1.1.0',
    techniqueIds: TECHNIQUE_IDS,
    supportsTechnique: supportsTechnique,
    maxPixels: MAX_PIXELS,
    render: render,
    getMetadata: function (canvas) { return metadata.get(canvas) || null; },
    visualProcesses: TEXTILE_PROCESSES
  });
})();
